// Ortak rapor export yardımcıları: stil verilmiş Excel ve PDF üretimi.
// Tüm rapor sekmeleri tek yerden formatlı çıktı alabilsin diye burada toplanır.

import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// AKIK kurumsal renk paleti
const COLORS = {
  primary: '#0F766E',      // turkuaz
  primaryDark: '#064E44',
  accentGold: '#B45309',
  accentBg: '#FEF3C7',
  headerBg: '#0F766E',
  headerFg: '#FFFFFF',
  totalBg: '#0F766E',
  totalFg: '#FFFFFF',
  zebra: '#F8FAFC',
  zebraAlt: '#FFFFFF',
  border: '#CBD5E1',
  groupBg: '#F1F5F9',
  groupFg: '#1E293B',
  refundFg: '#B91C1C',
  discountFg: '#7C3AED',
  positive: '#0F766E',
  muted: '#94A3B8'
};

const stripHash = (h) => h.replace('#', '');

// ---- META yardımcıları ----
const formatNow = () => {
  const d = new Date();
  return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
};

const sanitizeFileName = (name) => name.replace(/[^a-z0-9\-_]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toLowerCase();

const buildFileName = (title, ext) => {
  const ts = new Date().toISOString().slice(0, 10);
  return `${sanitizeFileName(title)}-${ts}.${ext}`;
};

// ---- EXCEL ----
// Stil yardımcıları
const cellStyle = (overrides = {}) => ({
  font: { name: 'Calibri', sz: 10, color: { rgb: '1E293B' }, ...overrides.font },
  alignment: { vertical: 'center', wrapText: false, ...overrides.alignment },
  border: overrides.border || {
    top: { style: 'thin', color: { rgb: stripHash(COLORS.border) } },
    bottom: { style: 'thin', color: { rgb: stripHash(COLORS.border) } },
    left: { style: 'thin', color: { rgb: stripHash(COLORS.border) } },
    right: { style: 'thin', color: { rgb: stripHash(COLORS.border) } }
  },
  fill: overrides.fill,
  numFmt: overrides.numFmt
});

const HEADER_STYLE = cellStyle({
  font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: stripHash(COLORS.headerFg) } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  fill: { fgColor: { rgb: stripHash(COLORS.headerBg) } }
});

const TOTAL_STYLE = cellStyle({
  font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: stripHash(COLORS.totalFg) } },
  fill: { fgColor: { rgb: stripHash(COLORS.totalBg) } }
});

const GROUP_STYLE = cellStyle({
  font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: stripHash(COLORS.groupFg) } },
  fill: { fgColor: { rgb: stripHash(COLORS.groupBg) } }
});

const SUBTOTAL_STYLE = cellStyle({
  font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: '475569' } },
  fill: { fgColor: { rgb: 'E2E8F0' } }
});

const META_LABEL_STYLE = cellStyle({
  font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: '475569' } },
  border: {}
});
const META_VALUE_STYLE = cellStyle({
  font: { name: 'Calibri', sz: 10, color: { rgb: '1E293B' } },
  border: {}
});
const TITLE_STYLE = cellStyle({
  font: { name: 'Calibri', sz: 16, bold: true, color: { rgb: stripHash(COLORS.primary) } },
  border: {}
});

// Hücre değerini Excel için normalize et: sayı/string/null
const normalizeCell = (v) => {
  if (v === null || v === undefined || v === '') return { v: '', t: 's' };
  if (typeof v === 'number') return { v, t: 'n' };
  if (typeof v === 'object' && 'value' in v) return v;
  return { v: String(v), t: 's' };
};

/**
 * Stil verilmiş Excel raporu üretir.
 * @param {Object} opts
 * @param {string} opts.title           - Rapor adı (örn. 'Kasiyer Bazlı Detaylı Rapor')
 * @param {string} opts.periodLabel     - Dönem etiketi (örn. 'Bu Ay (Mayıs 2026)')
 * @param {string} opts.storeLabel      - Mağaza filtresi etiketi
 * @param {string[]} opts.columns       - Sütun başlıkları
 * @param {Array}  opts.rows            - Veri satırları (her satır array; meta için { kind: 'group'|'subtotal'|'total', cells, span? })
 * @param {Object[]} [opts.colWidths]   - Sütun genişlikleri (örn. [{wch:14}, ...])
 * @param {number[]} [opts.numberCols]  - Para olarak biçimlenecek sütun indeksleri (₺#,##0.00)
 * @param {string}   [opts.sheetName]   - Sayfa adı
 */
export function exportReportToExcel(opts) {
  const {
    title,
    periodLabel = '',
    storeLabel = '',
    columns,
    rows,
    colWidths,
    numberCols = [],
    sheetName = 'Rapor'
  } = opts;

  const colCount = columns.length;
  const numFmtMoney = '"₺"#,##0.00';

  // AOA satırları + per-cell stil için ayrı bir map (XLSX-js-style cells)
  const aoa = [];

  // 1) Üst başlık
  aoa.push([{ v: title, t: 's' }]);
  // 2) Boş satır
  aoa.push(['']);
  // 3) Meta satırları
  aoa.push([{ v: 'Dönem:', t: 's' }, { v: periodLabel || '-', t: 's' }]);
  aoa.push([{ v: 'Mağaza:', t: 's' }, { v: storeLabel || '-', t: 's' }]);
  aoa.push([{ v: 'Oluşturulma:', t: 's' }, { v: formatNow(), t: 's' }]);
  aoa.push(['']);

  const headerRowIdx = aoa.length;
  aoa.push(columns.map(c => ({ v: c, t: 's' })));

  const dataStartRow = aoa.length;
  const meta = []; // { rowIdx, kind, span? }

  rows.forEach(r => {
    if (r && typeof r === 'object' && !Array.isArray(r) && r.kind) {
      const span = r.span || colCount;
      const row = Array(colCount).fill('');
      const cells = Array.isArray(r.cells) ? r.cells : [r.cells];
      // cells[0] = label (col 0, span sütunluk merge ile yayılır)
      // cells[1..] = dip toplam değerleri (span. sütundan başlayarak yerleşir)
      if (cells.length > 0) row[0] = cells[0];
      for (let i = 1; i < cells.length; i++) {
        const col = span + (i - 1);
        if (col < colCount) row[col] = cells[i];
      }
      meta.push({ rowIdx: aoa.length, kind: r.kind, span });
      aoa.push(row.map(normalizeCell));
    } else if (Array.isArray(r)) {
      aoa.push(r.map(normalizeCell));
    } else {
      aoa.push(Array(colCount).fill('').map(normalizeCell));
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Sütun genişlikleri
  ws['!cols'] = colWidths && colWidths.length === colCount
    ? colWidths
    : columns.map((c, i) => ({ wch: Math.max(12, Math.min(28, (c || '').length + 4)) }));

  // Dondurma: header altı
  ws['!freeze'] = { xSplit: 0, ySplit: headerRowIdx + 1 };
  ws['!views'] = [{ state: 'frozen', xSplit: 0, ySplit: headerRowIdx + 1 }];

  // Merge'ler
  ws['!merges'] = ws['!merges'] || [];
  // Başlık
  ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(0, colCount - 1) } });
  // Meta satırları için ikinci hücreyi (label hariç) merge etmek istemiyoruz, sade kalsın

  // Group/subtotal/total satırlarında label hücresini span boyunca merge et
  meta.forEach(m => {
    if (m.kind === 'group' || m.kind === 'total' || m.kind === 'subtotal') {
      ws['!merges'].push({ s: { r: m.rowIdx, c: 0 }, e: { r: m.rowIdx, c: Math.max(0, m.span - 1) } });
    }
  });

  // Hücre stilleri uygula
  const setStyle = (r, c, style) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (!ws[addr]) ws[addr] = { v: '', t: 's' };
    ws[addr].s = style;
  };

  // Başlık stili
  setStyle(0, 0, TITLE_STYLE);
  // Meta etiketleri
  for (let r = 2; r <= 4; r++) {
    setStyle(r, 0, META_LABEL_STYLE);
    setStyle(r, 1, META_VALUE_STYLE);
  }

  // Tablo başlığı
  for (let c = 0; c < colCount; c++) setStyle(headerRowIdx, c, HEADER_STYLE);

  // Veri satırları
  const moneyFmtCols = new Set(numberCols);
  for (let r = dataStartRow; r < aoa.length; r++) {
    const metaEntry = meta.find(m => m.rowIdx === r);
    const isGroup = metaEntry?.kind === 'group';
    const isSubtotal = metaEntry?.kind === 'subtotal';
    const isTotal = metaEntry?.kind === 'total';
    const isZebra = !isGroup && !isSubtotal && !isTotal && ((r - dataStartRow) % 2 === 1);
    for (let c = 0; c < colCount; c++) {
      let style;
      if (isTotal || isSubtotal) {
        // Label hücreleri (span aralığında) düz; değer hücrelerine sağ hizalı para formatı
        const base = isTotal ? TOTAL_STYLE : SUBTOTAL_STYLE;
        const isValueCell = c >= (metaEntry.span || colCount);
        style = isValueCell
          ? cellStyle({
            font: base.font,
            fill: base.fill,
            numFmt: moneyFmtCols.has(c) ? numFmtMoney : undefined,
            alignment: { vertical: 'center', horizontal: 'right' }
          })
          : base;
      } else if (isGroup) {
        style = GROUP_STYLE;
      } else {
        style = cellStyle({
          fill: isZebra ? { fgColor: { rgb: stripHash(COLORS.zebra) } } : undefined,
          numFmt: moneyFmtCols.has(c) ? numFmtMoney : undefined,
          alignment: { vertical: 'center', horizontal: moneyFmtCols.has(c) ? 'right' : 'left' }
        });
      }
      setStyle(r, c, style);
    }
  }

  // Satır yüksekliği başlık için biraz
  ws['!rows'] = ws['!rows'] || [];
  ws['!rows'][0] = { hpt: 26 };
  ws['!rows'][headerRowIdx] = { hpt: 22 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, buildFileName(title, 'xlsx'));
}

// ---- PDF ----
const hexToRgbArr = (h) => {
  const x = h.replace('#', '');
  return [parseInt(x.slice(0, 2), 16), parseInt(x.slice(2, 4), 16), parseInt(x.slice(4, 6), 16)];
};

const PDF_FONT_REGULAR = 'helvetica';

/**
 * Stil verilmiş PDF raporu üretir.
 * @param {Object} opts  - exportReportToExcel ile aynı arayüz
 * @param {string} [opts.subtitle]
 */
export function exportReportToPdf(opts) {
  const {
    title,
    periodLabel = '',
    storeLabel = '',
    columns,
    rows,
    numberCols = [],
    orientation = 'landscape',
    pageSize = 'a4',
    columnWidths,        // [{ cellWidth: 60 }, ...] — autoTable kolon stilleri
    bodyFontSize = 9,
    headFontSize = 9.5
  } = opts;

  const doc = new jsPDF({ orientation, unit: 'pt', format: pageSize });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ÜST BAŞLIK: AKIK + rapor adı
  const drawHeader = () => {
    // AKIK logosu (text marka)
    doc.setFillColor(...hexToRgbArr(COLORS.primary));
    doc.rect(0, 0, pageWidth, 64, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont(PDF_FONT_REGULAR, 'bold');
    doc.setFontSize(22);
    doc.text('AKIK', 36, 32);
    doc.setFontSize(9);
    doc.setFont(PDF_FONT_REGULAR, 'normal');
    doc.text('ERP', 36, 46);

    // Rapor adı (sağda)
    doc.setTextColor(255, 255, 255);
    doc.setFont(PDF_FONT_REGULAR, 'bold');
    doc.setFontSize(14);
    doc.text(title, pageWidth - 36, 32, { align: 'right' });
    doc.setFont(PDF_FONT_REGULAR, 'normal');
    doc.setFontSize(9);
    const metaLine = [
      periodLabel ? `Dönem: ${periodLabel}` : null,
      storeLabel ? `Mağaza: ${storeLabel}` : null,
      `Oluşturulma: ${formatNow()}`
    ].filter(Boolean).join('  •  ');
    doc.text(metaLine, pageWidth - 36, 50, { align: 'right' });
  };

  // ALT BİLGİ: sayfa numarası
  const drawFooter = (data) => {
    const pageCount = doc.internal.getNumberOfPages();
    doc.setTextColor(120);
    doc.setFontSize(8);
    doc.setFont(PDF_FONT_REGULAR, 'normal');
    doc.text(`Sayfa ${data.pageNumber} / ${pageCount}`, pageWidth - 36, pageHeight - 18, { align: 'right' });
    doc.text('AKIK ERP', 36, pageHeight - 18);
  };

  // autoTable body
  const moneyFmtSet = new Set(numberCols);
  const fmtMoney = (v) => (Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const body = [];
  const rowMeta = []; // index aligned with body

  rows.forEach(r => {
    if (r && typeof r === 'object' && !Array.isArray(r) && r.kind) {
      const span = r.span || columns.length;
      const cells = Array.isArray(r.cells) ? r.cells : [r.cells];
      const text = (cells[0] != null ? String(cells[0]) : '');
      const bodyRow = [{ content: text, colSpan: span, styles: r.kind === 'total'
        ? { fillColor: hexToRgbArr(COLORS.totalBg), textColor: [255, 255, 255], fontStyle: 'bold', halign: 'right', fontSize: 10 }
        : r.kind === 'group'
          ? { fillColor: hexToRgbArr(COLORS.groupBg), textColor: hexToRgbArr(COLORS.groupFg), fontStyle: 'bold', halign: 'left', fontSize: 10 }
          : { fillColor: [226, 232, 240], textColor: [71, 85, 105], fontStyle: 'bold', halign: 'right', fontSize: 9 }
      }];
      // Diğer hücreler total/subtotal için: dip toplam değerleri kalan hücrelerde varsa onlar da gelmeli
      if (cells.length > 1 && (r.kind === 'subtotal' || r.kind === 'total')) {
        // İlk colSpan span sütunu kaplar, kalanlar tek tek
        const extra = cells.slice(1).map((val) => ({
          content: typeof val === 'number' ? fmtMoney(val) : (val != null ? String(val) : ''),
          styles: r.kind === 'total'
            ? { fillColor: hexToRgbArr(COLORS.totalBg), textColor: [255, 255, 255], fontStyle: 'bold', halign: 'right', fontSize: 10 }
            : { fillColor: [226, 232, 240], textColor: [71, 85, 105], fontStyle: 'bold', halign: 'right', fontSize: 9 }
        }));
        bodyRow.push(...extra);
      }
      body.push(bodyRow);
      rowMeta.push(r.kind);
    } else if (Array.isArray(r)) {
      const arr = r.map((v, i) => {
        if (v == null || v === '') return '';
        if (typeof v === 'number' && moneyFmtSet.has(i)) return fmtMoney(v);
        if (typeof v === 'number') return v.toLocaleString('tr-TR');
        return String(v);
      });
      body.push(arr);
      rowMeta.push('row');
    }
  });

  // Sütun stilleri: numberCols için sağa hizala, columnWidths verildiyse genişlikleri uygula
  const computedColStyles = {};
  numberCols.forEach(c => { computedColStyles[c] = { ...(computedColStyles[c] || {}), halign: 'right' }; });
  if (Array.isArray(columnWidths)) {
    columnWidths.forEach((opts, idx) => {
      if (!opts) return;
      computedColStyles[idx] = { ...(computedColStyles[idx] || {}), ...opts };
    });
  }

  autoTable(doc, {
    startY: 76,
    margin: { top: 76, bottom: 36, left: 18, right: 18 },
    head: [columns],
    body,
    theme: 'grid',
    tableWidth: 'auto',
    styles: {
      font: PDF_FONT_REGULAR,
      fontSize: bodyFontSize,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      lineColor: hexToRgbArr(COLORS.border),
      lineWidth: 0.3,
      textColor: [30, 41, 59],
      overflow: 'linebreak',
      valign: 'middle'
    },
    headStyles: {
      fillColor: hexToRgbArr(COLORS.headerBg),
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      fontSize: headFontSize,
      cellPadding: { top: 4, right: 3, bottom: 4, left: 3 }
    },
    alternateRowStyles: {
      fillColor: hexToRgbArr(COLORS.zebra)
    },
    columnStyles: computedColStyles,
    didDrawPage: (data) => {
      drawHeader();
      drawFooter(data);
    }
  });

  doc.save(buildFileName(title, 'pdf'));
}

/**
 * Çoklu bölümlü rapor (dashboard) PDF — her bölüm kendi sütun yapısıyla.
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} [opts.periodLabel]
 * @param {string} [opts.storeLabel]
 * @param {Array}  opts.sections  - [{ heading, columns, rows, numberCols, layout?: 'kv'|'table' }]
 * @param {string} [opts.orientation]
 * @param {string} [opts.pageSize]
 */
export function exportSectionedReportToPdf(opts) {
  const { title, periodLabel = '', storeLabel = '', sections, orientation = 'portrait', pageSize = 'a4' } = opts;
  const doc = new jsPDF({ orientation, unit: 'pt', format: pageSize });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const drawHeader = () => {
    doc.setFillColor(...hexToRgbArr(COLORS.primary));
    doc.rect(0, 0, pageWidth, 64, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont(PDF_FONT_REGULAR, 'bold');
    doc.setFontSize(22);
    doc.text('AKIK', 36, 32);
    doc.setFontSize(9);
    doc.setFont(PDF_FONT_REGULAR, 'normal');
    doc.text('ERP', 36, 46);
    doc.setFont(PDF_FONT_REGULAR, 'bold');
    doc.setFontSize(14);
    doc.text(title, pageWidth - 36, 32, { align: 'right' });
    doc.setFont(PDF_FONT_REGULAR, 'normal');
    doc.setFontSize(9);
    const metaLine = [
      periodLabel ? `Dönem: ${periodLabel}` : null,
      storeLabel ? `Mağaza: ${storeLabel}` : null,
      `Oluşturulma: ${formatNow()}`
    ].filter(Boolean).join('  •  ');
    doc.text(metaLine, pageWidth - 36, 50, { align: 'right' });
  };

  const drawFooter = (data) => {
    const pageCount = doc.internal.getNumberOfPages();
    doc.setTextColor(120);
    doc.setFontSize(8);
    doc.setFont(PDF_FONT_REGULAR, 'normal');
    doc.text(`Sayfa ${data.pageNumber} / ${pageCount}`, pageWidth - 36, pageHeight - 18, { align: 'right' });
    doc.text('AKIK ERP', 36, pageHeight - 18);
  };

  const fmtMoney = (v) => (Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  let startY = 76;
  sections.forEach((section, sIdx) => {
    if (!section) return;
    const { heading, columns = [], rows = [], numberCols = [], layout = 'table' } = section;
    const moneyFmtSet = new Set(numberCols);

    // Bölüm başlığı: küçük renkli bant
    if (heading) {
      autoTable(doc, {
        startY,
        margin: { left: 24, right: 24, top: 76, bottom: 36 },
        head: [[heading]],
        body: [],
        theme: 'plain',
        styles: { fillColor: hexToRgbArr(COLORS.primary), textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, cellPadding: 6 },
        didDrawPage: (data) => {
          drawHeader();
          drawFooter(data);
        }
      });
      startY = doc.lastAutoTable.finalY + 4;
    }

    if (rows.length === 0) {
      startY += 4;
      return;
    }

    // Body
    const body = [];
    rows.forEach(r => {
      if (r && typeof r === 'object' && !Array.isArray(r) && r.kind) {
        const span = r.span || Math.max(1, columns.length);
        const cells = Array.isArray(r.cells) ? r.cells : [r.cells];
        const text = (cells[0] != null ? String(cells[0]) : '');
        const firstStyle = r.kind === 'total'
          ? { fillColor: hexToRgbArr(COLORS.totalBg), textColor: [255, 255, 255], fontStyle: 'bold', halign: 'right', fontSize: 10 }
          : r.kind === 'group'
            ? { fillColor: hexToRgbArr(COLORS.groupBg), textColor: hexToRgbArr(COLORS.groupFg), fontStyle: 'bold', halign: 'left', fontSize: 10 }
            : { fillColor: [226, 232, 240], textColor: [71, 85, 105], fontStyle: 'bold', halign: 'right', fontSize: 9 };
        const bodyRow = [{ content: text, colSpan: span, styles: firstStyle }];
        if (cells.length > 1 && (r.kind === 'subtotal' || r.kind === 'total')) {
          const extra = cells.slice(1).map((val) => ({
            content: typeof val === 'number' ? fmtMoney(val) : (val != null ? String(val) : ''),
            styles: r.kind === 'total'
              ? { fillColor: hexToRgbArr(COLORS.totalBg), textColor: [255, 255, 255], fontStyle: 'bold', halign: 'right', fontSize: 10 }
              : { fillColor: [226, 232, 240], textColor: [71, 85, 105], fontStyle: 'bold', halign: 'right', fontSize: 9 }
          }));
          bodyRow.push(...extra);
        }
        body.push(bodyRow);
      } else if (Array.isArray(r)) {
        body.push(r.map((v, i) => {
          if (v == null || v === '') return '';
          if (typeof v === 'number' && moneyFmtSet.has(i)) return fmtMoney(v);
          if (typeof v === 'number') return v.toLocaleString('tr-TR');
          return String(v);
        }));
      }
    });

    autoTable(doc, {
      startY,
      margin: { left: 24, right: 24, top: 76, bottom: 36 },
      head: columns.length ? [columns] : undefined,
      body,
      theme: 'grid',
      styles: { font: PDF_FONT_REGULAR, fontSize: 9, cellPadding: 4, lineColor: hexToRgbArr(COLORS.border), lineWidth: 0.3, textColor: [30, 41, 59] },
      headStyles: { fillColor: hexToRgbArr(COLORS.headerBg), textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 9.5 },
      alternateRowStyles: { fillColor: hexToRgbArr(COLORS.zebra) },
      columnStyles: layout === 'kv'
        ? { 0: { fontStyle: 'bold', textColor: [71, 85, 105] }, 1: { halign: 'right' } }
        : numberCols.reduce((acc, c) => { acc[c] = { halign: 'right' }; return acc; }, {}),
      didDrawPage: (data) => {
        drawHeader();
        drawFooter(data);
      }
    });
    startY = doc.lastAutoTable.finalY + 14;

    // Sayfa sonuna yaklaştıysak yeni sayfa
    if (startY > pageHeight - 80 && sIdx < sections.length - 1) {
      doc.addPage();
      startY = 76;
    }
  });

  doc.save(buildFileName(title, 'pdf'));
}

/**
 * Çoklu bölümlü Excel: her section ayrı sheet olarak yazılır.
 */
export function exportSectionedReportToExcel(opts) {
  const { title, periodLabel = '', storeLabel = '', sections } = opts;
  const wb = XLSX.utils.book_new();

  sections.forEach((section, idx) => {
    if (!section) return;
    const { heading = `Bölüm ${idx + 1}`, columns = [], rows = [], numberCols = [], colWidths } = section;
    const aoa = [];
    aoa.push([{ v: title, t: 's' }]);
    aoa.push(['']);
    aoa.push([{ v: 'Bölüm:', t: 's' }, { v: heading, t: 's' }]);
    aoa.push([{ v: 'Dönem:', t: 's' }, { v: periodLabel || '-', t: 's' }]);
    aoa.push([{ v: 'Mağaza:', t: 's' }, { v: storeLabel || '-', t: 's' }]);
    aoa.push([{ v: 'Oluşturulma:', t: 's' }, { v: formatNow(), t: 's' }]);
    aoa.push(['']);

    const headerRowIdx = aoa.length;
    if (columns.length) aoa.push(columns.map(c => ({ v: c, t: 's' })));

    const dataStartRow = aoa.length;
    const meta = [];
    const colCount = Math.max(1, columns.length);
    rows.forEach(r => {
      if (r && typeof r === 'object' && !Array.isArray(r) && r.kind) {
        const span = r.span || colCount;
        const row = Array(colCount).fill('');
        const cells = Array.isArray(r.cells) ? r.cells : [r.cells];
        cells.forEach((val, i) => { row[i] = val; });
        meta.push({ rowIdx: aoa.length, kind: r.kind, span });
        aoa.push(row.map(normalizeCell));
      } else if (Array.isArray(r)) {
        aoa.push(r.map(normalizeCell));
      } else {
        aoa.push(Array(colCount).fill('').map(normalizeCell));
      }
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = colWidths && colWidths.length === colCount
      ? colWidths
      : columns.map((c) => ({ wch: Math.max(14, Math.min(36, (c || '').length + 6)) }));
    ws['!freeze'] = { xSplit: 0, ySplit: headerRowIdx + 1 };
    ws['!views'] = [{ state: 'frozen', xSplit: 0, ySplit: headerRowIdx + 1 }];

    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(0, colCount - 1) } }
    ];
    meta.forEach(m => {
      if (m.kind === 'group' || m.kind === 'total') {
        ws['!merges'].push({ s: { r: m.rowIdx, c: 0 }, e: { r: m.rowIdx, c: Math.max(0, m.span - 1) } });
      }
    });

    const setStyle = (r, c, style) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { v: '', t: 's' };
      ws[addr].s = style;
    };

    setStyle(0, 0, TITLE_STYLE);
    for (let r = 2; r <= 5; r++) {
      setStyle(r, 0, META_LABEL_STYLE);
      setStyle(r, 1, META_VALUE_STYLE);
    }

    if (columns.length) {
      for (let c = 0; c < colCount; c++) setStyle(headerRowIdx, c, HEADER_STYLE);
    }

    const moneyFmtCols = new Set(numberCols);
    for (let r = dataStartRow; r < aoa.length; r++) {
      const metaEntry = meta.find(m => m.rowIdx === r);
      const isGroup = metaEntry?.kind === 'group';
      const isSubtotal = metaEntry?.kind === 'subtotal';
      const isTotal = metaEntry?.kind === 'total';
      const isZebra = !isGroup && !isSubtotal && !isTotal && ((r - dataStartRow) % 2 === 1);
      for (let c = 0; c < colCount; c++) {
        const style = isTotal ? TOTAL_STYLE
          : isGroup ? GROUP_STYLE
            : isSubtotal ? SUBTOTAL_STYLE
              : cellStyle({
                fill: isZebra ? { fgColor: { rgb: stripHash(COLORS.zebra) } } : undefined,
                numFmt: moneyFmtCols.has(c) ? '"₺"#,##0.00' : undefined,
                alignment: { vertical: 'center', horizontal: moneyFmtCols.has(c) ? 'right' : 'left' }
              });
        setStyle(r, c, style);
      }
    }

    ws['!rows'] = ws['!rows'] || [];
    ws['!rows'][0] = { hpt: 26 };
    if (columns.length) ws['!rows'][headerRowIdx] = { hpt: 22 };

    XLSX.utils.book_append_sheet(wb, ws, heading.slice(0, 31).replace(/[\\/?*[\]:]/g, ''));
  });

  XLSX.writeFile(wb, buildFileName(title, 'xlsx'));
}

// Tek noktadan iki formatı da ihraç
export function exportReport(format, opts) {
  if (format === 'pdf') return exportReportToPdf(opts);
  return exportReportToExcel(opts);
}

// Para biçim yardımcısı (rapor builder'lar için)
export const exportFmtMoney = (n) => (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
